import React, {
    ReactElement,
} from "react";
import {
    Editor, Location, Node, Transforms,
} from "slate";
import { ReactEditor } from "slate-react";
import { User, useGetMentionUsersQuery } from "../../__generated__/api";
import Avatar from "../avatar/Avatar";
import { StyleType } from "./blocks/base";
import KeyboardEventHandler from "./keyboard-event-handler";
import { ToolbarList } from "./ToolbarList";

export interface MentionedUser {
    user: User;
    isInChannel: boolean;
}

interface MentionBarProps {
    editor: ReactEditor;
    keyboardEvents: KeyboardEventHandler;
    sourceId: string;
    onChange?: (user: MentionedUser) => void;
}

const STANDARD_REPLACEMENT_SET: Record<string, string> = {
    "@here": "@here",
    "@channel": "@channel",

};

export default function MentionBar({
    editor, keyboardEvents, sourceId, onChange,
}: MentionBarProps): ReactElement | null {
    const { data, loading } = useGetMentionUsersQuery({ variables: { id: sourceId } });

    if (loading || !data || !data.settings?.agents) {
        return null;
    }

    const usersInChannel = new Set<string>(data.conversationSourceUsers?.toIdList("id"));
    const allUsers = data.conversationSourceUsers?.concat(data.settings.agents).dedupe(row => row.id);

    const replaceTextResetState = (value: string, startSelection: Location) => {
        const range = startSelection ? Editor.range(editor, startSelection as Location, editor.selection?.anchor) : undefined;
        if (!range) {
            return;
        }

        const user = allUsers?.find(current => current?.id === value);
        if (user && startSelection) {
            Transforms.insertText(editor, ` `, {
                at: Editor.end(editor, []),
            });

            Transforms.move(editor, {
                distance: -1,
                unit: "character",
            });

            Transforms.insertNodes(
                editor,
                {
                    [StyleType.MENTION]: {
                        avatarUrl: user?.avatarUrl as string,
                        name: user?.name as string,
                        id: user?.id as string,
                    },
                    text: user?.name as string,
                } as unknown as Node,
                {
                    mode: "lowest",
                    hanging: false,
                    voids: true,
                    at: range,
                },
            );

            onChange?.({
                user: user as User,
                isInChannel: usersInChannel.has(user.id),
            });
        }
    };

    const filter = (text: string) => {
        const query = text.toLowerCase().substr(1);

        const filteredUsers = allUsers?.filter(user => user?.name?.toLocaleLowerCase()?.includes(query)
                || user?.group?.toLocaleLowerCase()?.includes(query)) ?? [];

        return filteredUsers.filterNull().map(user => ({
            value: user.id as string,
            label: (
                <>
                    <Avatar src={user.avatarUrl as string} variant="xs" />
                    {" "}
                    {user.name}
                </>
            ),
        }));
    };

    return (
        <ToolbarList
            editor={editor}
            keyboardEvents={keyboardEvents}
            startKey="@"
            onSelected={replaceTextResetState}
            filter={filter}
            replacementSet={STANDARD_REPLACEMENT_SET}
        />
    );
}

MentionBar.defaultProps = {
    onChange: undefined,
};
